import { ReactNode, useEffect, useRef, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Button, typography } from '@joor/design-system'
import '@joor/design-system/dist/designTokens/variables.css'
import { isEqual } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useRenderToasters } from 'hooks/useRenderToasters'
import { isAccountTypeBrand, userIsLoggedIn } from 'store/currentUser/selectors'
import { INIT_PANEL, TOGGLE_EDIT_MODE } from 'store/storefront/constants'
import {
  getAccountId,
  getEditModeEnabled,
  getInMenuPages,
  getNotInMenuPages,
  getPanel,
  getTotalPages,
  hasPages,
} from 'store/storefront/selectors'

import { STOREFRONT } from 'routes/paths'

import PageItem from './PageItem'
import {
  EMPTY_SECTION_DROP_HEIGHT,
  MAX_ONLINE_IN_MENU_PAGES,
  MAX_PAGES,
  PAGES_SECTION_IN_MENU_LIST,
  PAGES_SECTION_NOT_IN_MENU_LIST,
  SECTION_DROP_HEIGHT,
} from './Pages.constants'
import { useBulkUpdateStorefront } from './Pages.hooks'
import { NewPage, PageElement, SaveOrder } from './Pages.types'
import {
  calculateNewOrder,
  getDefaultPageName,
  getNewPageOrderNbr,
  pagesCollisionDetection,
} from './Pages.utils'
import { useStorefrontNavigation } from 'features/Storefront/Storefront.hooks'
import { useUpsertStorefront } from 'features/Storefront/StorefrontNavbar/StorefrontNavbar.hooks'

export const CREATE_ID = 'storefront_create_page'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 28px;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  > button {
    margin: 24px auto 16px;
    padding: 15px 10px;
  }
`
const DropArea = styled.div<{ isEmpty: boolean }>`
  min-height: ${({ isEmpty }) =>
    isEmpty ? EMPTY_SECTION_DROP_HEIGHT : SECTION_DROP_HEIGHT}px;
`

/**
 * A page section that can receive a drop. Registering the section itself - and
 * not only the pages inside it - is what lets an *empty* section be a drop
 * target: with no pages in it there is nothing else for dnd-kit to report as
 * `over`, so a page could never be dragged into it.
 */
const DroppableContainer = ({
  children,
  inMenu,
  isEmpty,
  testId,
}: {
  children: ReactNode
  inMenu: boolean
  isEmpty: boolean
  testId: string
}) => {
  const { setNodeRef } = useDroppable({ id: testId, data: { inMenu } })

  return (
    <DropArea ref={setNodeRef} isEmpty={isEmpty} data-testid={testId}>
      {children}
    </DropArea>
  )
}
const Title = styled.span`
  height: 60px;
  line-height: 60px;
  margin-left: 24px;
  font-size: 36px !important;
  ${typography.alpha.primaryActive.headline1};
`
const Subtitle = styled.span`
  margin-left: 24px;
  ${typography.alpha.primaryActive.body1};
`

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--color-primary-200);
  margin-bottom: 16px;
`

const PageManagement = styled.div`
  display: flex;
  flex-direction: column;
  h3 {
    ${typography.alpha.primaryActive.body2};
    padding-left: 24px;
    &:last-of-type {
      margin-top: 24px;
    }
  }
  > button {
    margin: 24px auto 16px;
  }
`
const Description = styled.p`
  ${typography.alpha.primaryActive.caption};
  padding: 0 8px 0 24px;
`

const Pages = () => {
  const inMenu: PageElement[] = useSelector(getInMenuPages)
  const notInMenu: PageElement[] = useSelector(getNotInMenuPages)
  const hasStorefrontPages = useSelector<boolean>(hasPages)
  const panel = useSelector(getPanel)
  const editMode = useSelector(getEditModeEnabled)
  const totalPages = useSelector(getTotalPages)
  const isLoggedIn = useSelector<boolean>(userIsLoggedIn)
  const isBrandAccount = useSelector<boolean>(isAccountTypeBrand)
  const brandId = useSelector(getAccountId)
  const [orderedInMenu, setOrderedInMenu] = useState(inMenu)
  const [orderedNotInMenu, setOrderedNotInMenu] = useState(notInMenu)
  const onlineInMenu =
    inMenu.filter((page) => page.isOnline).length >= MAX_ONLINE_IN_MENU_PAGES
  const [inMenuLimit, setInMenuLimit] = useState(onlineInMenu)
  // The section a drag started from. `active.data.current.inMenu` cannot be used
  // at drop time: `onDragOver` has already re-rendered the item into the other
  // list, so by then dnd-kit reports its *destination* section.
  const dragOriginInMenu = useRef<boolean | null>(null)
  const navigate = useNavigate()
  const { renderErrorToast, renderSuccessToast } = useRenderToasters()
  const dispatch = useDispatch()

  const { refetch: refetchStorefrontNavigation, loading: loadingNavigation } =
    useStorefrontNavigation(isLoggedIn, isBrandAccount, brandId)
  const { loading: loadingNewPage, upsertStorefront: createNewPage } =
    useUpsertStorefront()
  const { bulkUpdate } = useBulkUpdateStorefront()
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  useEffect(() => {
    setOrderedInMenu(inMenu)
  }, [inMenu])

  useEffect(() => {
    setInMenuLimit(onlineInMenu)
  }, [inMenu])

  useEffect(() => {
    setOrderedNotInMenu(notInMenu)
  }, [notInMenu])

  const buttonCreateNewPageDisabled = loadingNavigation || loadingNewPage

  const handleOnCreateNewPageClick = async () => {
    if (totalPages === MAX_PAGES) {
      return renderErrorToast({
        description:
          'Maximum number of 25 pages already reached. Please delete an existing page to create a new one.',
      })
    }

    const { displayName: nextDisplayName, uniqueName: nextUniqueName } =
      getDefaultPageName([...orderedInMenu, ...orderedNotInMenu])
    const newPageConfig: NewPage = {
      isHome: !hasStorefrontPages,
      orderNbr: getNewPageOrderNbr([...orderedInMenu, ...orderedNotInMenu]),
      displayName: nextDisplayName,
      uniqueName: nextUniqueName,
      isOnline: false,
      isNewPage: true,
    }

    try {
      const result = await createNewPage(newPageConfig)
      if (!result?.data || result?.errors) {
        return renderErrorToast({
          description: 'There was an error creating your new page',
        })
      }

      await refetchStorefrontNavigation()
      const {
        id,
        isHome,
        orderNbr,
        displayName,
        uniqueName,
        isOnline,
        brandId: brandIdFromResponse,
      } = result?.data?.upsertStorefront

      dispatch({
        type: INIT_PANEL,
        value: {
          ...panel,
          id,
          isHome,
          orderNbr,
          displayName,
          uniqueName,
          isOnline,
          brandId: brandIdFromResponse,
        },
      })
      navigate(
        generatePath(STOREFRONT, {
          accountId: brandId,
          pageName: uniqueName,
        }),
      )
      renderSuccessToast({ description: 'Page created.' })
    } catch {
      renderErrorToast({
        description: 'There was an error creating your new page',
      })
    }
  }

  const saveOrder: SaveOrder = async (
    isInMenu,
    firstBlockPages,
    differentBlocks = false,
    secondBlockPages,
  ) => {
    let bulkUpdateInput
    const formatInput = (p: PageElement) => {
      const { id, orderNbr } = p
      return { id, orderNbr }
    }
    if (differentBlocks) {
      const newInMenuOrder = calculateNewOrder(firstBlockPages, true)
      const newNotInMenuOrder = calculateNewOrder(
        secondBlockPages as PageElement[],
        false,
      )
      bulkUpdateInput = [
        ...newInMenuOrder.map(formatInput),
        ...newNotInMenuOrder.map(formatInput),
      ]
    } else {
      const newInMenuOrder = calculateNewOrder(firstBlockPages, isInMenu)
      bulkUpdateInput = [...newInMenuOrder.map(formatInput)]
    }
    try {
      const { data, errors } = await bulkUpdate(bulkUpdateInput)
      if (!data || errors) {
        renderErrorToast({
          description: 'There was an error updating the order of your pages.',
        })
      }
    } catch {
      renderErrorToast({
        description: 'There was an error updating the order of your pages.',
      })
    }
  }

  const onDragStart = (event: DragStartEvent) => {
    dragOriginInMenu.current = Boolean(event.active.data.current?.inMenu)
  }

  // To handle dragging from one section to another
  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const [activeData, overData] = [active.data.current, over.data.current]

    if (activeData?.inMenu === overData?.inMenu) {
      return
    }

    if (activeData?.inMenu) {
      const element = orderedInMenu.find((item) => item.id === active.id)
      if (!element) {
        return
      }
      setOrderedInMenu(orderedInMenu.filter((item) => item.id !== active.id))
      setOrderedNotInMenu([...orderedNotInMenu, element])
    } else {
      const element = orderedNotInMenu.find((item) => item.id === active.id)
      if (!element) {
        return
      }
      setOrderedNotInMenu(
        orderedNotInMenu.filter((item) => item.id !== active.id),
      )
      setOrderedInMenu([...orderedInMenu, element])
    }
  }

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    const originInMenu = dragOriginInMenu.current
    dragOriginInMenu.current = null

    // `onDragOver` has already moved the item into its destination list, so the
    // local lists - not `active.data.current.inMenu` - say where the drop landed.
    const isInMenu = orderedInMenu.some((page) => page.id === active.id)
    const movedBetweenSections =
      originInMenu !== null && originInMenu !== isInMenu

    let inMenuPages = orderedInMenu
    let notInMenuPages = orderedNotInMenu

    const droppedOnAnotherPage =
      !!over && active.id !== over.id && over.data.current?.inMenu === isInMenu

    if (droppedOnAnotherPage) {
      const items = isInMenu ? orderedInMenu : orderedNotInMenu
      const oldIndex = items.findIndex((page) => page.id === active.id)
      const newIndex = items.findIndex((page) => page.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const ordered = arrayMove(items, oldIndex, newIndex)
        if (isInMenu) {
          inMenuPages = ordered
          setOrderedInMenu(ordered)
        } else {
          notInMenuPages = ordered
          setOrderedNotInMenu(ordered)
        }
      }
    }

    // A section change must be persisted whatever the drop landed on - dropping
    // on the item's own slot, or on empty space, still moves it between sections.
    if (movedBetweenSections) {
      const movedPage = inMenuPages.find((page) => page.id === active.id)

      if (isInMenu && inMenuLimit && movedPage?.isOnline) {
        setOrderedInMenu(inMenu)
        setOrderedNotInMenu(notInMenu)
        return renderErrorToast({
          description: 'There is a limit of 10 online pages in Menu.',
        })
      }

      return saveOrder(isInMenu, inMenuPages, true, notInMenuPages)
    }

    const reordered = isInMenu ? inMenuPages : notInMenuPages
    if (!isEqual(reordered, isInMenu ? inMenu : notInMenu)) {
      await saveOrder(isInMenu, reordered, false)
    }
  }

  return (
    <PageContainer>
      <Title>Pages</Title>
      <Subtitle>Manage the pages in you profile</Subtitle>
      <Button
        disabled={buttonCreateNewPageDisabled}
        onClick={handleOnCreateNewPageClick}
        iconName="add"
        data-testid={CREATE_ID}
      >
        Create New Page
      </Button>
      <Separator />
      <PageManagement>
        <DndContext
          sensors={sensors}
          collisionDetection={pagesCollisionDetection}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
        >
          <h3>In Menu</h3>
          <SortableContext
            items={orderedInMenu.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <DroppableContainer
              testId={PAGES_SECTION_IN_MENU_LIST}
              inMenu
              isEmpty={orderedInMenu.length === 0}
            >
              {orderedInMenu.map((item) => (
                <PageItem
                  data-testid={`storefront_in_menu_item_${item.uniqueName}`}
                  onClick={() => {
                    if (item.id === panel.id) {
                      return
                    }
                    if (editMode) {
                      dispatch({ type: TOGGLE_EDIT_MODE })
                    }
                    navigate(
                      generatePath(STOREFRONT, {
                        accountId: brandId,
                        pageName: item.isHome ? null : item.uniqueName,
                      }),
                    )
                  }}
                  {...item}
                  key={item.id}
                  inMenu
                  loadingNavigation={loadingNavigation}
                  refetchStorefrontNavigation={refetchStorefrontNavigation}
                />
              ))}
            </DroppableContainer>
          </SortableContext>
          <h3>Not In Menu</h3>
          <Description>
            These pages are hidden from the navigation and can be linked to
            images and/or text in your profile.
          </Description>
          <SortableContext
            items={orderedNotInMenu.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <DroppableContainer
              testId={PAGES_SECTION_NOT_IN_MENU_LIST}
              inMenu={false}
              isEmpty={orderedNotInMenu.length === 0}
            >
              {orderedNotInMenu.map((item) => (
                <PageItem
                  onClick={() => {
                    if (item.id === panel.id) {
                      return
                    }
                    if (editMode) {
                      dispatch({ type: TOGGLE_EDIT_MODE })
                    }
                    navigate(
                      generatePath(STOREFRONT, {
                        accountId: brandId,
                        pageName: !item.isHome ? item.uniqueName : '',
                      }),
                    )
                  }}
                  {...item}
                  key={item.id}
                  loadingNavigation={loadingNavigation}
                  refetchStorefrontNavigation={refetchStorefrontNavigation}
                  data-testid={`storefront_not_in_menu_item_${item.uniqueName}`}
                />
              ))}
            </DroppableContainer>
          </SortableContext>
        </DndContext>
      </PageManagement>
    </PageContainer>
  )
}

export default Pages
